import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DOCUMENT,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { IdaChatbotConsentPanelWebComponent } from './ida-chatbot-consent-panel/ida-chatbot-consent-panel';

import { Store } from '@ngrx/store';
import {
  ConsentType,
  GetConsent,
  getConsent,
  getIsConsentLoading,
  getLegalTexts,
  getSelectedCustomerIdent,
  getUser,
  SetConsent,
} from '@resident-nx/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderWebComponent } from '@resident-nx/web/ui';
import { INFRASTRUCTURE_CONFIG, KeycloakService } from '@resident-nx/web/data-access';

@Component({
  standalone: true,
  selector: 'rs-web-ida-chatbot',
  templateUrl: 'ida-chatbot.html',
  styleUrl: 'ida-chatbot.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [IdaChatbotConsentPanelWebComponent, TranslateModule, LoaderWebComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdaChatbotWebComponent implements OnInit {
  #environment = inject(INFRASTRUCTURE_CONFIG).environment;
  #store = inject(Store);
  #keycloakService = inject(KeycloakService);
  #translate = inject(TranslateService);
  #document = inject(DOCUMENT);
  #window = this.#document?.defaultView;

  private customerIdent = this.#store.selectSignal(getSelectedCustomerIdent);
  private isChatbotInjected = false;

  private consent = this.#store.selectSignal(getConsent);
  legalTexts = this.#store.selectSignal(getLegalTexts);
  isConsentLoading = this.#store.selectSignal(getIsConsentLoading);
  user = this.#store.selectSignal(getUser);
  isPanelOpen = signal(false);
  #token = this.#keycloakService.tokenSignal;

  hasConsent = computed(() => this.consent()?.CHATBOT_USAGE?.acceptedLatest);
  // Read-only support mode: chatting as the impersonated resident is not
  // allowed, so the panel shows a hint instead of the chatbot.
  isImpersonating = this.#keycloakService.isImpersonating;

  constructor() {
    effect(() => {
      const hasConsent = this.hasConsent();
      const isPanelOpen = this.isPanelOpen();
      const isImpersonating = this.isImpersonating();
      void untracked(async () => {
        if (hasConsent && isPanelOpen && !isImpersonating && !this.isChatbotInjected) {
          await this.injectChatbot();
        }
      });
    });

    effect(() => {
      const token = this.#token();

      untracked(() => {
        if (this.isChatbotInjected) {
          this.#window['initializeWithData']({
            token,
          });
        }
      });
    });
  }

  ngOnInit() {
    this.#store.dispatch(GetConsent({ consentType: ConsentType.CHATBOT_USAGE }));
  }

  togglePanel() {
    this.isPanelOpen.set(!this.isPanelOpen());
  }

  acceptConsent(): void {
    this.#store.dispatch(
      SetConsent({
        consentType: ConsentType.CHATBOT_USAGE,
        consentId: this.legalTexts().chatbotUsageId,
        consentGiven: true,
      })
    );
  }

  private injectChatbot(): Promise<void> {
    const welcomeMessage = this.#translate.instant('chat_bot.welcome_l');
    const apiUrl = this.#environment.chatbotApiUrl;
    const customerId = this.customerIdent();
    const userInitials = this.user()?.initials;
    const environment = this.#environment.envName;
    const debug = false;
    return new Promise(resolve => {
      const script = this.#document.createElement('script');
      script.type = 'text/javascript';
      script.src = '/web/components/ai-chatbot/main-b6ed6741.js';
      this.#window['immomioInitData'] = {
        token: this.#token(),
        customerId,
        apiUrl,
        debug,
        welcomeMessage,
        userInitials,
        environment,
      };
      this.#document.head.appendChild(script);
      this.isChatbotInjected = true;
      resolve();
    });
  }
}
